import { CommonModule } from '@angular/common';
import { animate, AnimationEvent, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, HostBinding, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatLegacyCardModule } from '@angular/material/legacy-card';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';

import { IconComponent } from '@mode/capra';

export enum ManageAccessAlertType {
  Success = 'success',
  Error = 'error',
  Warn = 'warn',
}

export interface ManageAccessAlertMessage {
  text: string;
  type: ManageAccessAlertType;
  confirm?: boolean;
}

@Component({
  standalone: true,
  selector: 'mode-alert-banner',
  imports: [CommonModule, IconComponent, MatLegacyCardModule],
  templateUrl: './alert-banner.component.html',
  styleUrls: ['./alert-banner.component.scss'],
  animations: [
    trigger('fadeOut', [
      transition(':leave', [style({ opacity: 1 }), animate('300ms ease-out', style({ opacity: 0 }))]),
    ]),
  ],
})
export class AlertBannerComponent implements OnChanges, OnInit, AfterViewInit {
  @HostBinding('class')
  classes = 'mode-manage-access-alert';

  @Input()
  message: ManageAccessAlertMessage = { text: '', type: ManageAccessAlertType.Success };

  isError: boolean | undefined = false;
  isWarn: boolean | undefined = false;
  isConfirm: boolean | undefined = false;
  text: string | undefined = '';
  displayActions = false;

  viewInitialized$ = new BehaviorSubject(false);
  messageReady$ = new BehaviorSubject(false);

  showMessage$: Observable<boolean> = of(false);

  ngAfterViewInit() {
    this.viewInitialized$.next(true);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['message'] && changes['message'].currentValue) {
      this.setMessage(changes['message'].currentValue);
      this.displayActions = this.canDisplayActions();
      this.showMessage();
    } else {
      this.hideMessage();
    }
  }

  ngOnInit() {
    // before we show the message and start the animation we need to wait for
    // 1) the view to be initialized
    // 2) a message to be available
    this.showMessage$ = combineLatest([this.messageReady$, this.viewInitialized$]).pipe(
      delay(0),
      map(([messageReady, viewInitialized]) => messageReady && viewInitialized)
    );
  }

  handleAlertClick() {
    if (!this.isConfirm) {
      this.hideMessage();
    }
  }

  canDisplayActions() {
    return !!this.isConfirm;
  }

  setMessage(message?: ManageAccessAlertMessage) {
    if (message) {
      this.text = message.text;
      this.isError = message.type === ManageAccessAlertType.Error;
      this.isWarn = message.type === ManageAccessAlertType.Warn;
      this.isConfirm = !!message.confirm;
    } else {
      this.text = undefined;
      this.isError = undefined;
      this.isWarn = undefined;
      this.isConfirm = undefined;
    }
  }

  showMessage() {
    this.messageReady$.next(true);
  }

  hideMessage() {
    this.messageReady$.next(false);
  }

  fadeFinished($event: AnimationEvent) {
    const isEnterEvent = $event.fromState === 'void' && $event.toState === null;
    const isLeaveEvent = $event.fromState === null && $event.toState === 'void';

    if (isLeaveEvent) {
      this.setMessage();
    } else if (isEnterEvent && !this.isError && !this.isConfirm) {
      setTimeout(() => {
        this.hideMessage();
      }, 3000);
    }
  }
}
